/**
 * CookingMode Component
 *
 * Fullscreen cooking mode with step-by-step recipe instructions.
 * Features:
 * - Automatic fullscreen entry
 * - Step navigation (next/previous)
 * - Screen wake lock to prevent dimming
 * - Exit to recipe detail view
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecipeStore } from '../../stores/recipeStore';
import { Timer } from './Timer';
import './cooking.css';

interface CookingModeProps {
  recipeId: number;
}

export function CookingMode({ recipeId }: CookingModeProps) {
  const { t } = useTranslation('cooking');
  const navigate = useNavigate();
  const { currentRecipe, fetchRecipe, isLoading, error } = useRecipeStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Parse instructions into steps
  const parseInstructions = (instructions: string): string[] => {
    const steps = instructions
      .split(/\n+/)
      .map(step => step.trim())
      .filter(step => step.length > 0)
      .map(step => {
        // Remove leading numbers and periods
        return step.replace(/^\d+\.\s*/, '');
      });

    return steps.length > 0 ? steps : [instructions];
  };

  const instructionSteps = currentRecipe ? parseInstructions(currentRecipe.instructions) : [];

  // Fetch recipe on mount
  useEffect(() => {
    if (recipeId) {
      fetchRecipe(recipeId);
    }
  }, [recipeId, fetchRecipe]);

  // Request fullscreen on mount
  useEffect(() => {
    const requestFullscreen = async () => {
      if (!containerRef.current) return;

      try {
        if (document.fullscreenElement) {
          // Already in fullscreen
          return;
        }

        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else {
          setFullscreenError(t('fullscreenNotSupported'));
        }
      } catch (err) {
        console.error('Failed to enter fullscreen:', err);
        setFullscreenError(t('fullscreenFailed'));
      }
    };

    requestFullscreen();

    // Exit fullscreen on unmount
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(err => {
          console.error('Failed to exit fullscreen:', err);
        });
      }
    };
  }, [t]);

  // Request wake lock on mount
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          const lock = await navigator.wakeLock.request('screen');
          wakeLockRef.current = lock;

          // Handle wake lock release
          lock.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }
      } catch (err) {
        console.error('Failed to request wake lock:', err);
      }
    };

    requestWakeLock();

    // Release wake lock on unmount
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(err => {
          console.error('Failed to release wake lock:', err);
        });
      }
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        handleNextStep();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        handlePreviousStep();
      } else if (e.key === 'Escape') {
        handleExit();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, instructionSteps.length]);

  const handleNextStep = () => {
    if (currentStep < instructionSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleExit = async () => {
    // Exit fullscreen
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(err => {
        console.error('Failed to exit fullscreen:', err);
      });
    }

    // Release wake lock
    if (wakeLockRef.current) {
      await wakeLockRef.current.release().catch(err => {
        console.error('Failed to release wake lock:', err);
      });
    }

    // Navigate back to recipe detail
    navigate(`/recipes/${recipeId}`);
  };

  if (error) {
    return (
      <div className="cooking-mode-container" ref={containerRef}>
        <div className="cooking-mode-error" role="alert">
          <h2>{t('errorLoadingRecipe')}</h2>
          <p>{error}</p>
          <button onClick={handleExit} className="btn-primary">
            {t('exitCookingMode')}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !currentRecipe) {
    return (
      <div className="cooking-mode-container" ref={containerRef}>
        <div className="cooking-mode-loading" role="status" aria-live="polite">
          <p>{t('loadingRecipe')}</p>
        </div>
      </div>
    );
  }

  const progress = ((currentStep + 1) / instructionSteps.length) * 100;

  return (
    <div className="cooking-mode-container" ref={containerRef}>
      {/* Header */}
      <header className="cooking-mode-header">
        <div className="cooking-mode-title">
          <h1>{currentRecipe.title}</h1>
          <p className="cooking-mode-servings">{t('servings', { count: currentRecipe.servings })}</p>
        </div>
        <button
          onClick={handleExit}
          className="cooking-mode-exit"
          aria-label="Exit cooking mode"
        >
          {'✕ '}{t('exit')}
        </button>
      </header>

      {/* Fullscreen error notification */}
      {fullscreenError && (
        <div className="cooking-mode-notification" role="alert">
          {fullscreenError}
        </div>
      )}

      {/* Progress Bar */}
      <div className="cooking-mode-progress-bar" aria-label="Recipe progress">
        <div
          className="cooking-mode-progress-fill"
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={currentStep + 1}
          aria-valuemin={1}
          aria-valuemax={instructionSteps.length}
          aria-label={t('stepOf', { current: currentStep + 1, total: instructionSteps.length })}
        />
      </div>

      {/* Step Counter */}
      <div className="cooking-mode-step-counter" role="status" aria-live="polite" aria-atomic="true">
        {t('stepOf', { current: currentStep + 1, total: instructionSteps.length })}
      </div>

      {/* Current Step */}
      <main className="cooking-mode-step-content" role="main" aria-label="Current cooking step">
        <div className="cooking-mode-step-number" aria-hidden="true">{currentStep + 1}</div>
        <p className="cooking-mode-step-text">{instructionSteps[currentStep]}</p>
      </main>

      {/* Timer Component */}
      <Timer stepIndex={currentStep} defaultDuration={10} />

      {/* Navigation Controls */}
      <nav className="cooking-mode-controls" aria-label="Step navigation">
        <button
          onClick={handlePreviousStep}
          disabled={currentStep === 0}
          className="cooking-mode-nav-button"
          aria-label={`Go to previous step${currentStep === 0 ? ' (disabled)' : ''}`}
        >
          <span aria-hidden="true">←</span> {t('previous')}
        </button>
        <button
          onClick={handleNextStep}
          disabled={currentStep === instructionSteps.length - 1}
          className="cooking-mode-nav-button cooking-mode-nav-button-primary"
          aria-label={`Go to next step${currentStep === instructionSteps.length - 1 ? ' (disabled)' : ''}`}
        >
          {t('next')} <span aria-hidden="true">→</span>
        </button>
      </nav>

      {/* Keyboard Hints */}
      <div className="cooking-mode-hints" role="complementary" aria-label="Keyboard shortcuts">
        <span>{t('keyboardHints')}</span>
      </div>
    </div>
  );
}

export default CookingMode;
